/**
 * @fileoverview Mock backend for the account-activation endpoints.
 * @description Registers POST /identity/activation/mfa/verify and POST
 * /identity/activation/mfa/resend on the feature's shared mock adapter (see auth-mock-adapter.ts).
 * Both only accept an ACTIVATION challenge (opened by accepting an invitation, or by signing in to
 * a registered account — see invitation-mock-store.ts and auth-mock.service.ts); verifying the
 * demo code (123456, from challenge-store.ts) marks the user ACTIVE so their next sign-in is a
 * normal login. The appCodes mirror the real backend's (1046-1053). A no-op when
 * VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { authMockAdapter } from './auth-mock-adapter'
import {
  deleteChallenge,
  getChallenge,
  incrementFailedAttempts,
  rotateChallenge,
} from './challenge-store'
import { buildMockErrorEnvelope, buildMockSuccessEnvelope } from './mock-envelope.utils'
import { activateMockUser, findMockUserById } from './mock-user-directory'

import type { MockChallenge } from './challenge-store'
import type {
  ResendActivationCodeRequest,
  ResendActivationCodeResponse,
  VerifyActivationCodeRequest,
  VerifyActivationCodeResponse,
} from '@/features/auth/services/activation.service'

export const ACTIVATION_RESEND_COOLDOWN_SECONDS = 30

const MAX_FAILED_ATTEMPTS = 5
const MAX_RESENDS = 5

const CHALLENGE_INVALID_ERROR = buildMockErrorEnvelope(
  400,
  '1046',
  'Activation challenge is invalid or no longer usable.',
)

/**
 * @description Looks up a pending activation challenge, ignoring login challenges.
 * @param {string} challengeId - The challenge id from the request.
 * @returns {MockChallenge | null} The activation challenge, or null.
 */
function getActivationChallenge(challengeId: string): MockChallenge | null {
  const challenge = getChallenge(challengeId)
  return challenge?.purpose === 'ACTIVATION' ? challenge : null
}

if (authMockAdapter) {
  authMockAdapter.onPost('/identity/activation/mfa/verify').reply((config) => {
    const { challengeId, otp } = JSON.parse(config.data as string) as VerifyActivationCodeRequest
    const challenge = getActivationChallenge(challengeId)

    if (!challenge) {
      return [400, CHALLENGE_INVALID_ERROR]
    }

    if (Date.now() > challenge.expiresAt) {
      return [400, buildMockErrorEnvelope(400, '1048', 'Activation code has expired.')]
    }

    if (otp !== challenge.code) {
      const updated = incrementFailedAttempts(challengeId)

      if (updated && updated.failedAttempts >= MAX_FAILED_ATTEMPTS) {
        deleteChallenge(challengeId)
        return [
          403,
          buildMockErrorEnvelope(403, '1049', 'Activation verification attempts exhausted.'),
        ]
      }

      return [400, buildMockErrorEnvelope(400, '1047', 'Activation code is incorrect.')]
    }

    deleteChallenge(challengeId)
    activateMockUser(challenge.userId)

    const response: VerifyActivationCodeResponse = {
      activated: true,
      loginRequired: true,
      nextStep: 'LOGIN',
    }
    return [200, buildMockSuccessEnvelope(response, 'Account activated')]
  })

  authMockAdapter.onPost('/identity/activation/mfa/resend').reply((config) => {
    const { challengeId } = JSON.parse(config.data as string) as ResendActivationCodeRequest
    const existing = getActivationChallenge(challengeId)

    if (!existing) {
      return [400, CHALLENGE_INVALID_ERROR]
    }

    if (existing.resendCount >= MAX_RESENDS) {
      deleteChallenge(challengeId)
      return [403, buildMockErrorEnvelope(403, '1051', 'Activation resend limit exceeded.')]
    }

    if (Date.now() < existing.resendAvailableAt) {
      return [429, buildMockErrorEnvelope(429, '1050', 'Activation resend cooldown is active.')]
    }

    const rotated = rotateChallenge(challengeId)

    if (!rotated) {
      return [400, CHALLENGE_INVALID_ERROR]
    }

    const user = findMockUserById(rotated.userId)
    const response: ResendActivationCodeResponse = {
      challengeId: rotated.challengeToken,
      channel: 'EMAIL',
      maskedDestination: user ? user.email.replace(/^(.).*(@.*)$/, '$1***$2') : '',
      expiresInSeconds: Math.round((rotated.expiresAt - Date.now()) / 1000),
      resendAvailableInSeconds: ACTIVATION_RESEND_COOLDOWN_SECONDS,
    }
    return [200, buildMockSuccessEnvelope(response, 'Activation code resent')]
  })
}
